import { string } from "prop-types";
import { forwardRef, useCallback, useMemo, useRef } from "react";
import CurrencyInput from "react-currency-input-field";

import styled from "styled-components/macro";
import { themeSizes } from "theme";

import FnrColorSquare from "UI/FnrColorSquare";

import { ensureLeadingHex } from "Utilities/Colors/colorUtils";
import { formatPhoneNumber } from "Utilities/Number";
import formatTaxId from "Utilities/Number/formatTaxId";
import retainInputCursorPos from "Utilities/retainInputCursorPos";
import {
  decToPercent,
  percentToDec,
  percentageRegExp,
} from "Utilities/stringTransformers";

import TranslateIn from "./TranslateIn";
import { defaultSize } from "./constants";
import fnrInputTypes from "./fnrInputTypes";
import { shiftRight } from "./interpolations";

const InputSwitch = forwardRef(({ typeArg, ...rest }, ref) => {
  switch (typeArg) {
    case fnrInputTypes.currency:
      return <SCurrencyInput {...rest} ref={ref} />;
    case fnrInputTypes.hexcode:
      return (
        <TranslateIn.Container>
          <SInput {...rest} ref={ref} />
          <TranslateIn.ElementContainer $size={rest.$size}>
            <FnrColorSquare
              backgroundColor={rest.value}
              circle
              size={themeSizes.sm}
            />
          </TranslateIn.ElementContainer>
        </TranslateIn.Container>
      );
    default:
      return <SInput {...rest} ref={ref} />;
  }
});

InputSwitch.propTypes = { typeArg: string };

const Input = forwardRef(
  (
    {
      value,
      setValue,
      id,
      icon,
      borderColor,
      backgroundColor = "#fff",
      textColor = "#333",
      borderRadius,
      focusColor,
      focusSize,
      onEnter,
      size = defaultSize,
      type: typeArg,
      onChange,
      onFocus,
      onBlur: onBlurArg,
      fullWidth, // not used, but should be omitted from rest
      minHeight, // not used, but should be omitted from rest
      maxHeight, // not used, but should be omitted from rest
      iconColor, // not used, but should be omitted from rest
      maxWidth, // not used, but should be omitted from rest
      label,
      charLimit,
      setErrorText,
      allowDecimals = true,
      alignLeft,
      flexType,
      stout,
      noBorder,
      ...rest
    },
    refArg
  ) => {
    const localRef = useRef();
    const ref = refArg || localRef;

    const handleChange = useCallback(
      (e) => {
        if (charLimit && charLimit < e.target.value.length) {
          setErrorText(`Only ${charLimit} characters are allowed`);
          if (setValue) setValue(e.target.value.slice(0, charLimit));
          return;
        }
        setErrorText("");
        if (onChange) onChange(e);
        else if (setValue) setValue(e.target.value);
      },
      [charLimit, onChange, setErrorText, setValue]
    );

    const onBlur = (e) => {
      if (onBlurArg) onBlurArg(e);
    };

    const onKeyUp = (e) => {
      if (e.which === 13 && onEnter) onEnter(e);
    };

    const diffentPropsObj = useMemo(() => {
      switch (typeArg) {
        case fnrInputTypes.email:
          return {
            onChange: ({ target: { value: newValue = "" } }) => {
              setValue(newValue);
            },
            placeholder: "example@example.com",
          };
        case fnrInputTypes.phone:
          return {
            onChange: ({ target: { value: newValue = "", selectionEnd } }) => {
              if (flexType && /[^\d-]/.test(newValue)) {
                const typingAtEnd = selectionEnd === value.length + 1;
                if (typingAtEnd) setValue(newValue.replace(/-/g, ""));
                else setValue(newValue);
              } else setValue(formatPhoneNumber(newValue));
              retainInputCursorPos(ref.current, { ignoreIfAtEnd: true });
            },
            value: value ? formatPhoneNumber(value) : "",
          };
        case fnrInputTypes.taxid:
          return {
            onChange: ({ target: { value: newValue = "" } }) => {
              setValue(formatTaxId(newValue));
              retainInputCursorPos(ref.current, { ignoreIfAtEnd: true });
            },
            value: value ? formatTaxId(value) : "",
            placeholder: "XX-XXXXXXX",
            inputMode: "numeric",
          };
        case fnrInputTypes.hexcode:
          return {
            onChange: ({ target: { value: newValue = "" } }) => {
              setValue(ensureLeadingHex(newValue));
            },
            value: value ? ensureLeadingHex(value) : "",
          };
        case fnrInputTypes.currency:
          return {
            onValueChange: (newValue) =>
              handleChange({ target: { value: newValue } }),
            prefix: "$",
            decimalScale: allowDecimals ? 2 : 0,
            allowNegativeValue: false,
            $alignRight: alignLeft !== true,
            placeholder: allowDecimals ? "$0.00" : "$0",
          };
        case fnrInputTypes.percentage:
          return {
            onChange: ({ target: { value: newValue = "" } }) => {
              if (!percentageRegExp.test(newValue)) {
                retainInputCursorPos(ref.current, {
                  backOne: true, // What the user typed will not be set, so put their cursor back where it was.
                  suffixLength: 1,
                });
                return;
              }
              let finalValue = percentToDec(newValue);
              setValue(finalValue);
              // If what they typed was valid, retain the cursor position, but do not go beyond the percent sign.
              retainInputCursorPos(ref.current, { suffixLength: 1 });
            },
            $alignRight: true,
            value: value ? decToPercent(value) : "",
            placeholder: label ? "" : "0.0%",
          };
        default:
          return { onChange: handleChange };
      }
    }, [
      typeArg,
      allowDecimals,
      alignLeft,
      label,
      value,
      handleChange,
      setValue,
      ref,
    ]);
    const type = useMemo(() => {
      switch (typeArg) {
        case fnrInputTypes.currency:
        case fnrInputTypes.percentage:
        case fnrInputTypes.hexcode:
          return "text";
        default:
          return typeArg;
      }
    }, [typeArg]);

    return (
      <InputSwitch
        typeArg={typeArg}
        data-cy="fnrInput2"
        value={value} // overridded by percentage.
        id={id}
        $hasIcon={Boolean(icon)}
        $borderColor={borderColor}
        $backgroundColor={backgroundColor}
        $textColor={textColor}
        $borderRadius={borderRadius}
        $focusColor={focusColor}
        $focusSize={focusSize}
        $stout={stout}
        $noBorder={noBorder}
        onKeyUp={onKeyUp}
        ref={ref}
        $size={size}
        type={type}
        onBlur={onBlur}
        onFocus={onFocus}
        label={label}
        role="textbox"
        {...rest}
        {...diffentPropsObj}
      />
    );
  }
);

export default Input;

const padding = ({ $size, $stout, $noBorder }) => {
  if ($noBorder) return "padding: 4px 2px;";
  switch ($size) {
    case themeSizes.xs:
      return "padding: 4px 8px;";
    case themeSizes.sm:
      return "padding: 8px 12px;";
    case themeSizes.lg:
      return `padding: ${$stout ? "13.25" : "16"}px 20px;`;
    case themeSizes.xl:
      return "padding: 18px 24px;";
    case themeSizes.md: // also default
    default:
      return "padding: 12px 16px;";
  }
};

const fontSize = ({ $size }) => {
  switch ($size) {
    case themeSizes.lg:
      return "font-size: 20px;";
    case themeSizes.xl:
      return "font-size: 28px;";
    case themeSizes.md: // also default
    case themeSizes.xs:
    case themeSizes.sm:
    default:
      return "font-size: 18px;";
  }
};

const border = ({ $noBorder, $borderColor, colors, $stout }) => {
  if ($noBorder) return "border: none;";
  if ($stout)
    return `border: 2px solid ${colors[$borderColor] || $borderColor};`;
  return `border: 1px solid ${colors[$borderColor] || $borderColor};`;
};

const inputStyles = ({
  $stout,
  $noBorder,
  $borderRadius = $stout ? "12px" : "10px",
  $borderColor = "fnGrey",
  $size,
  $textColor,
  $backgroundColor,
  $focusColor = "#278dc1",
  $focusSize = "1px",
  $alignRight,
  theme: { colors },
}) => `
  font-weight: 400;
  width: 100%;
  box-sizing: border-box;
  ${fontSize({ $size })}
  border-radius: ${$borderRadius};
  ${border({ $noBorder, colors, $stout, $borderColor })}
  ${padding({ $size, $stout, $noBorder })}
  color: ${$textColor};
  outline: none;
  background: ${$backgroundColor};
  transition: border-color ${
    $stout ? ".6" : ".2"
  }s, box-shadow .2s, border-radius 0.3s;
  margin: 0;
  font-family: inherit;
  ${$alignRight ? "text-align: right;" : ""}

  &:focus${$stout ? ", :hover" : ""} {
    ${
      $stout
        ? `border-color: ${colors[$focusColor] || $focusColor};`
        : `box-shadow: 0 0 0 ${$focusSize} ${
            colors[$focusColor] || $focusColor
          }b5;`
    }
    transition: border-color .2s, box-shadow .2s, border-radius 0.3s;
  }

  &::placeholder {
    color: ${$textColor};
    opacity: 0.7;
  }
`;

const SInput = styled.input`
  ${inputStyles}
  padding-left: ${shiftRight};
  ${({ $paddingRight }) =>
    $paddingRight ? `padding-right: ${$paddingRight}` : ""};
`;

const currencyIndent = ({ $size }) => {
  switch ($size) {
    case themeSizes.lg:
      return "text-indent: 10.5px;";
    case themeSizes.xl:
      return "text-indent: 15px;";
    case themeSizes.md: // also default
    case themeSizes.xs:
    case themeSizes.sm:
    default:
      return "text-indent: 9.25px;";
  }
};

const SCurrencyInput = styled(CurrencyInput)`
  &[value=""]::placeholder {
    text-indent: 0px;
  }
  &[value=""] {
    ${currencyIndent}
  }
  ${inputStyles}
`;
